import { gql } from '@apollo/client'
import {
  CheckRoundIcon,
  DeleteIcon,
  DislikeActiveIcon,
  DislikeIcon,
  Dropdown,
  EditIcon,
  FlagIcon,
  IconButton,
  MenuItem,
  MoreVerticalIcon,
  Spinner,
  useToast,
} from '@farol-ds/react'
import {
  ARTIFACT_LABEL,
  useDeleteDocument,
  UseDocumentModerationModalReturn,
  UseDocumentReportModalReturn,
} from '@jusbrasil-web/docview/core'
import { MouseEvent } from 'react'

import { useVoteComment } from '../../hooks/use-vote-comment'
import {
  DropdownMoreActions_CommentFragment,
  DropdownMoreActions_DocumentFragment,
  DropdownMoreActions_ViewerFragment,
} from './__generated__/dropdown-more-actions.graphql'

export interface DropdownMoreActionsProps {
  document: DropdownMoreActions_DocumentFragment
  comment: DropdownMoreActions_CommentFragment
  viewer: DropdownMoreActions_ViewerFragment | null
  documentReport: UseDocumentReportModalReturn
  documentModeration: UseDocumentModerationModalReturn
  onReport: () => void
  onModeration: () => void
  onEdit?: () => void
}

export const DropdownMoreActions = ({
  documentReport,
  documentModeration,
  onReport,
  onModeration,
  document,
  comment,
  viewer,
  onEdit,
}: DropdownMoreActionsProps) => {
  const { id, viewerDownVoted } = comment
  const vote = viewerDownVoted ? 'NONE' : 'NEGATIVE'
  const voteComment = useVoteComment({ comment })
  const toast = useToast()
  const deleteComment = useDeleteDocument({
    document,
    docId: comment.id,
    artifact: ARTIFACT_LABEL.COMENTARIO,
  })

  const isAuthor = !!comment.profile?.isViewer
  const isAdmin = !!viewer?.user?.isAdmin

  async function handleDownVote() {
    if (!viewer) return
    await voteComment
      .vote({
        variables: {
          input: {
            commentId: id,
            vote,
          },
        },
      })
      .catch((error) => {
        console.log(error)
      })
  }

  async function handleDelete(event: MouseEvent<HTMLDivElement>) {
    event.preventDefault()
    await deleteComment
      .deleteDocument()
      .then(() => toast('Comentário deletado pelo autor', { id: 'delete-comment' }))
      .catch(() => toast('Erro ao deletado comentário', { type: 'negative' }))
  }

  return (
    <Dropdown
      onOpenChange={(open) => {
        if (open) {
          documentReport.init()
          if (isAdmin) {
            documentModeration.init()
          }
        }
      }}
    >
      <Dropdown.Trigger>
        <IconButton icon={<MoreVerticalIcon />} label="Mais" size="sm" />
      </Dropdown.Trigger>

      <Dropdown.Menu
        title="Mais opções"
        /* no autofocus after interaction with the dropdown (then close), better UX since the items open modals */
        onCloseAutoFocus={(evt) => evt.preventDefault()}
      >
        <Dropdown.MenuItem onClick={handleDownVote}>
          <MenuItem>
            <MenuItem.Content>
              <MenuItem.Leading>
                {voteComment.loading ? (
                  <Spinner size="xs" />
                ) : viewerDownVoted ? (
                  <DislikeActiveIcon />
                ) : (
                  <DislikeIcon />
                )}
              </MenuItem.Leading>
              Não recomendar
            </MenuItem.Content>
          </MenuItem>
        </Dropdown.MenuItem>

        <Dropdown.MenuItem onClick={onReport} disabled={!documentReport.ready}>
          <MenuItem>
            <MenuItem.Content>
              <MenuItem.Leading>
                {documentReport.loading ? <Spinner size="xs" /> : <FlagIcon />}
              </MenuItem.Leading>
              Reportar
            </MenuItem.Content>
          </MenuItem>
        </Dropdown.MenuItem>

        {(isAdmin || isAuthor) && (
          <>
            <Dropdown.MenuItem onClick={onEdit}>
              <MenuItem>
                <MenuItem.Content>
                  <MenuItem.Leading>
                    <EditIcon />
                  </MenuItem.Leading>
                  Editar
                </MenuItem.Content>
              </MenuItem>
            </Dropdown.MenuItem>

            {isAdmin && (
              <Dropdown.MenuItem onClick={onModeration} disabled={!documentModeration.ready}>
                <MenuItem>
                  <MenuItem.Content>
                    <MenuItem.Leading>
                      {documentModeration.loading ? <Spinner size="xs" /> : <CheckRoundIcon />}
                    </MenuItem.Leading>
                    Moderar
                  </MenuItem.Content>
                </MenuItem>
              </Dropdown.MenuItem>
            )}

            <Dropdown.MenuItem onClick={handleDelete}>
              <MenuItem type="danger">
                <MenuItem.Content>
                  <MenuItem.Leading>
                    {deleteComment.loading ? <Spinner size="xs" /> : <DeleteIcon />}
                  </MenuItem.Leading>
                  Deletar
                </MenuItem.Content>
              </MenuItem>
            </Dropdown.MenuItem>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

DropdownMoreActions.fragments = {
  comment: gql`
    fragment DropdownMoreActions_comment on DocumentComment {
      viewerDownVoted
      profile {
        isViewer
      }
      ...UseVoteComment_comment
    }
    ${useVoteComment.fragments.comment}
  `,
  document: gql`
    fragment DropdownMoreActions_document on Document {
      ...UseDeleteDocument_document
    }
    ${useDeleteDocument.fragments.document}
  `,
  viewer: gql`
    fragment DropdownMoreActions_viewer on Profile {
      pid
      user {
        isAdmin
      }
    }
  `,
}
